import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import "./SpssPage.css";

function SpssPage() {
  const [file, setFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // SPSS Modal state
  const [showModeModal, setShowModeModal] = useState(false);
  const [fileType, setFileType] = useState(""); // 'transaction' | 'recruitment' | 'other'

  // CSV Modal state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFileType, setCsvFileType] = useState(""); // 'survey_questions' | 'variable_values' | 'other'

  const navigate = useNavigate();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleCsvFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  /**
   * Called when user clicks "Upload SPSS".
   * Detects file name prefix and shows modal accordingly.
   */
  const handleFileUpload = () => {
    if (!file) return alert("Please select an SPSS file to upload");

    const nameLower = file.name.toLowerCase();
    if (nameLower.startsWith("transaction")) {
      setFileType("transaction");
      setShowModeModal(true);
    } else if (nameLower.startsWith("recruitment")) {
      setFileType("recruitment");
      setShowModeModal(true);
    } else {
      // For other files, just upload directly with 'update' mode
      setFileType("other");
      doUpload("update");
    }
  };

  /**
   * Called from modal when user selects Truncate or Update.
   * For recruitment files, 'truncate' option is not shown — only update.
   */
  const handleModeSelect = (mode) => {
    setShowModeModal(false);
    doUpload(mode);
  };

  const doUpload = async (uploadMode) => {
    const formData = new FormData();
    formData.append("spssFile", file);
    formData.append("uploadMode", uploadMode);
    setIsLoading(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/upload-spss`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessage(response.data.message || "File uploaded successfully!");
      alert("File uploaded successfully!");
    } catch (error) {
      setMessage("File upload failed. Please try again.");
      console.error(error.response ? error.response.data : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Called when user clicks "Upload CSV".
   * Detects filename and shows modal for survey_questions / variable_values.
   */
  const handleCsvUpload = () => {
    if (!csvFile) return alert("Please select a CSV file to upload");

    const nameWithoutExt = csvFile.name.toLowerCase().replace(/\.(csv|xlsx)$/, "").trim();
    if (nameWithoutExt === "survey_questions") {
      setCsvFileType("survey_questions");
      setShowCsvModal(true);
    } else if (nameWithoutExt === "variable_values") {
      setCsvFileType("variable_values");
      setShowCsvModal(true);
    } else {
      setCsvFileType("other");
      doCsvUpload("truncate"); // default behaviour for unknown CSVs (original truncate logic)
    }
  };

  const handleCsvModeSelect = (mode) => {
    setShowCsvModal(false);
    doCsvUpload(mode);
  };

  const doCsvUpload = async (uploadMode) => {
    const formData = new FormData();
    formData.append("csvFile", csvFile);
    formData.append("uploadMode", uploadMode);
    setIsLoading(true);
    setMessage("");

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/upload-csv`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessage(response.data.message || "CSV file uploaded successfully!");
      alert("CSV file uploaded successfully!");
    } catch (error) {
      setMessage("CSV upload failed. Please try again.");
      console.error(error.response ? error.response.data : error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    navigate("/dashboards");
  };

  return (
    <div className="upload-container">
      <button onClick={goBack} className="spss-back-button">
        &#8592; Back
      </button>
      <h2 className="upload-title">Upload Files</h2>

      {/* SPSS Upload */}
      <input
        type="file"
        accept=".sav"
        onChange={handleFileChange}
        className="file-input"
      />
      <button
        onClick={handleFileUpload}
        className="spss-filter-button"
        disabled={!file || isLoading}
      >
        {isLoading ? "Uploading..." : "Upload SPSS"}
      </button>

      {/* CSV / XLSX Upload */}
      <input
        type="file"
        accept=".csv,.xlsx"
        onChange={handleCsvFileChange}
        className="file-input"
      />
      <button
        onClick={handleCsvUpload}
        className="spss-filter-button"
        disabled={!csvFile || isLoading}
      >
        {isLoading ? "Uploading..." : "Upload CSV / XLSX"}
      </button>

      {message && (
        <p style={{ textAlign: "center", color: "#007bff" }}>{message}</p>
      )}

      {/* ── SPSS Upload Mode Modal ────────────────────────────────────────── */}
      {showModeModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3 className="modal-title">Choose Upload Mode</h3>

            {fileType === "transaction" && (
              <p className="modal-desc">
                <strong>Transaction</strong> file detected.
                <br />
                <em>Truncate</em> will clear <code>table_1</code> before
                inserting. <em>Append</em> will add new rows.
              </p>
            )}

            {fileType === "recruitment" && (
              <p className="modal-desc">
                <strong>Recruitment</strong> file detected.
                <br />
                <em>Truncate</em> will drop all existing{" "}
                <code>recruitment_table_*</code> data and clear their{" "}
                <code>vtm</code> entries before uploading.
                <br />
                <em>Append</em> will add new rows to existing tables and
                insert new <code>vtm</code> mappings.
              </p>
            )}

            <div className="modal-actions">
              {(fileType === "transaction" || fileType === "recruitment") && (
                <button
                  className="modal-btn modal-btn-danger"
                  onClick={() => handleModeSelect("truncate")}
                >
                  Truncate &amp; Upload
                </button>
              )}
              <button
                className="modal-btn modal-btn-primary"
                onClick={() => handleModeSelect("update")}
              >
                Append
              </button>
              <button
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowModeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Upload Mode Modal ─────────────────────────────────────────── */}
      {showCsvModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3 className="modal-title">Choose Upload Mode</h3>

            {csvFileType === "survey_questions" && (
              <p className="modal-desc">
                <strong>survey_questions</strong> file detected.
                <br />
                <em>Truncate</em> will clear the <code>survey_questions</code>{" "}
                table before inserting.
                <br />
                <em>Append</em> will add new rows to the existing table.
              </p>
            )}

            {csvFileType === "variable_values" && (
              <p className="modal-desc">
                <strong>variable_values</strong> file detected.
                <br />
                Expected columns:{" "}
                <code>Variable_name, Value, Statement_1, Visible_Notvisible</code>.
                <br />
                <em>Truncate</em> will clear the <code>variable_values</code>{" "}
                table before inserting.
                <br />
                <em>Append</em> will add new rows to the existing table.
              </p>
            )}

            <div className="modal-actions">
              <button
                className="modal-btn modal-btn-danger"
                onClick={() => handleCsvModeSelect("truncate")}
              >
                Truncate &amp; Upload
              </button>
              <button
                className="modal-btn modal-btn-primary"
                onClick={() => handleCsvModeSelect("append")}
              >
                Append
              </button>
              <button
                className="modal-btn modal-btn-secondary"
                onClick={() => setShowCsvModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpssPage;